import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import './index.css';

import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from './lib/queryClient.js';
import { authApi } from './api/auth.js';
import { tokenStore } from './api/client.js';

import Sidebar from './components/Sidebar';
import Topbar from './components/Topbar';

import LoginPage from './pages/LoginPage';
import AdminDashboard from './pages/admin/AdminDashboard';
import TLDashboard from './pages/teamleader/TLDashboard';
import CustomersPage from './pages/shared/CustomersPage';
import AdminCustomersPage from './pages/admin/AdminCustomersPage';
import CustomerDetail from './pages/CustomerDetail';
import NewCustomersPage from './pages/shared/NewCustomersPage';
import CorporateCustomersPage from './pages/shared/CorporateCustomersPage';
import OrdersPage from './pages/shared/OrdersPage';
import StaffsPage from './pages/admin/StaffsPage';
import TeamStaffsPage from './pages/shared/TeamStaffsPage';
import SalesTeamManagementPage from './pages/admin/SalesTeamManagementPage';
import SalesRepCustomersPage from './pages/shared/SalesRepCustomersPage';
import RolesPage from './pages/admin/RolesPage';
import AreasPage from './pages/admin/AreasPage';
import ParentAreasPage from './pages/admin/ParentAreasPage';
import CitiesPage from './pages/admin/CitiesPage';
import AlertsPage from './pages/shared/AlertsPage';
import ReportsPage from './pages/shared/ReportsPage';
import SettingsPage from './pages/shared/SettingsPage';
import PlaceOrderPage from './pages/PlaceOrderPage';
import OrderDetailPage from './pages/shared/OrderDetailPage';
import OrganisationsPage from './pages/admin/OrganisationsPage';
import ProductsPage from './pages/admin/ProductsPage';
import ProductCustomizationsPage from './pages/admin/ProductCustomizationsPage';
import ProductCategoriesPage from './pages/admin/ProductCategoriesPage';
import ProductStockPage from './pages/admin/ProductStockPage';
import CallAdminPage from './pages/admin/CallAdminPage';
import NotOrderingAdminPage from './pages/admin/NotOrderingAdminPage';
import DailyCallTrackerPage from './pages/admin/DailyCallTrackerPage';
import DailyCallTrackerDetailPage from './pages/admin/DailyCallTrackerDetailPage';
import VisitAdminPage from './pages/admin/VisitAdminPage';
import SystemSettingsPage from './pages/admin/SystemSettingsPage';
import OrderImportPage from './pages/admin/OrderImportPage';
import CallQueueImportPage from './pages/admin/CallQueueImportPage';
import ProductCostImportPage from './pages/admin/ProductCostImportPage';
import OrderApprovalsPage from './pages/admin/OrderApprovalsPage';
import CustomerImportPage from './pages/admin/CustomerImportPage';
import LiveTrackerPage from './pages/admin/LiveTrackerPage';
import DistanceHistoryPage from './pages/admin/DistanceHistoryPage';
import { NAV_GROUPS } from './nav.js';

const ADMIN_NAV = ['dashboard', 'customers', 'retail-customers', 'corporate-customers', 'team-customers', 'orders', 'order-approvals?type=new', 'order-approvals?type=edit', 'place-order', 'organisations', 'products', 'products/customizations', 'products/categories', 'warehouse/stock', 'staffs', 'team-management', 'roles', 'cities', 'parent-areas', 'areas', 'alerts', 'reports', 'settings', 'crm-call-management', 'visit-management', 'live-tracker', 'distance-history', 'system-settings', 'daily-call-tracker', 'not-ordering', 'order-import', 'call-queue-import', 'product-cost-import', 'customer-import'];
const TL_NAV = ['dashboard', 'customers', 'retail-customers', 'corporate-customers', 'team-customers', 'orders', 'place-order', 'staffs', 'alerts', 'settings'];

// { itemId -> required roles[] } derived from nav.js — single source of truth
const ROLE_RESTRICTED = Object.fromEntries(
  NAV_GROUPS.flatMap(g => g.items)
    .filter(item => item.roles)
    .map(item => [item.id, item.roles])
);

function ProtectedLayout({ user, setUser }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  const fullPath = location.pathname.substring(1) || 'dashboard';
  const page = fullPath.split('/')[0] || 'dashboard';
  const baseNav = (user?.role === 'teamleader' || user?.role === 'sales_teamlead' || user?.role === 'tl') ? TL_NAV : ADMIN_NAV;
  const allowedNav = baseNav.filter(id => {
    const required = ROLE_RESTRICTED[id];
    return !required || required.includes(user?.role);
  });

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Redirect to dashboard if user navigates to a page they don't have access to
  useEffect(() => {
    const isAllowed = allowedNav.some(n => {
      const basePath = n.split('?')[0];
      return basePath === fullPath || fullPath.startsWith(basePath + '/');
    }) || fullPath === 'place-order' || fullPath.startsWith('orders/');
    if (user && !isAllowed) {
      navigate('/dashboard', { replace: true });
    }
  }, [page, allowedNav, user, navigate]);

  if (!user) return <Navigate to="/login" replace />;

  const handleLogout = async () => {
    try {
      await authApi.logout();
    } catch (e) {
      console.error('Logout failed:', e);
    }
    setUser(null);
    navigate('/login');
  };

  const handleNavigate = (p) => {
    setSidebarOpen(false);
    if (p === 'login') { handleLogout(); return; }
    navigate(`/${p}`);
  };

  const renderDashboard = () => {
    if (user.role === 'teamleader' || user.role === 'sales_teamlead' || user.role === 'tl') return <TLDashboard user={user} onNavigate={handleNavigate} />;
    return <AdminDashboard user={user} onNavigate={handleNavigate} />;
  };

  return (
    <div className="app-shell">
      <Sidebar
        activePage={fullPath}
        onNavigate={handleNavigate}
        allowedNav={allowedNav}
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
      />
      <div className="main-content">
        <Topbar
          page={page}
          user={user}
          onNavigate={handleNavigate}
          onMenuClick={() => setSidebarOpen(true)}
        />
        <Routes>
          <Route path="/dashboard" element={renderDashboard()} />
          <Route path="/customers" element={user.role === 'admin' ? <AdminCustomersPage /> : <CustomersPage role={user.role} />} />
          <Route path="/customers/:id" element={<CustomerDetail />} />
          <Route path="/retail-customers" element={<NewCustomersPage role={user.role} />} />
          <Route path="/corporate-customers" element={<CorporateCustomersPage role={user.role} />} />
          <Route path="/orders" element={<OrdersPage role={user.role} />} />
          <Route path="/orders/:orderId" element={<OrderDetailPage />} />
          <Route path="/order-approvals" element={user.role === 'admin' ? <OrderApprovalsPage /> : renderDashboard()} />
          <Route path="/place-order" element={<PlaceOrderPage user={user} />} />
          <Route path="/organisations" element={['admin', 'sales_tl', 'tl'].includes(user.role) ? <OrganisationsPage user={user} /> : renderDashboard()} />
          <Route path="/staffs" element={user.role === 'admin' ? <StaffsPage /> : (['teamleader', 'sales_teamlead', 'sales_tl', 'tl'].includes(user.role) ? <TeamStaffsPage /> : renderDashboard())} />
          <Route path="/team-customers" element={<SalesRepCustomersPage user={user} />} />
          <Route path="/team-management" element={user.role === 'admin' ? <SalesTeamManagementPage /> : renderDashboard()} />
          <Route path="/roles" element={user.role === 'admin' ? <RolesPage /> : renderDashboard()} />
          <Route path="/cities"       element={user.role === 'admin' ? <CitiesPage />       : renderDashboard()} />
          <Route path="/parent-areas" element={user.role === 'admin' ? <ParentAreasPage /> : renderDashboard()} />
          <Route path="/areas"        element={user.role === 'admin' ? <AreasPage />        : renderDashboard()} />
          <Route path="/products" element={user.role === 'admin' ? <ProductsPage /> : renderDashboard()} />
          <Route path="/products/customizations" element={user.role === 'admin' ? <ProductCustomizationsPage /> : renderDashboard()} />
          <Route path="/products/categories" element={user.role === 'admin' ? <ProductCategoriesPage /> : renderDashboard()} />
          <Route path="/warehouse/stock" element={user.role === 'admin' ? <ProductStockPage /> : renderDashboard()} />
          <Route path="/crm-call-management" element={user.role === 'admin' ? <CallAdminPage /> : renderDashboard()} />
          <Route path="/daily-call-tracker" element={user.role === 'admin' ? <DailyCallTrackerPage /> : renderDashboard()} />
          <Route path="/daily-call-tracker/:date/:userId" element={user.role === 'admin' ? <DailyCallTrackerDetailPage /> : renderDashboard()} />
          <Route path="/not-ordering" element={user.role === 'admin' ? <NotOrderingAdminPage /> : renderDashboard()} />
          <Route path="/visit-management" element={user.role === 'admin' ? <VisitAdminPage /> : renderDashboard()} />
          <Route path="/live-tracker" element={['admin', 'sales_tl'].includes(user.role) ? <LiveTrackerPage user={user} /> : renderDashboard()} />
          <Route path="/distance-history" element={['admin', 'sales_tl'].includes(user.role) ? <DistanceHistoryPage /> : renderDashboard()} />
          <Route path="/system-settings" element={user.role === 'admin' ? <SystemSettingsPage /> : renderDashboard()} />
          <Route path="/order-import"         element={['admin', 'software_developer'].includes(user.role) ? <OrderImportPage />         : renderDashboard()} />
          <Route path="/call-queue-import"   element={['admin', 'software_developer'].includes(user.role) ? <CallQueueImportPage />   : renderDashboard()} />
          <Route path="/product-cost-import" element={['admin', 'software_developer'].includes(user.role) ? <ProductCostImportPage /> : renderDashboard()} />
          <Route path="/customer-import"    element={['admin', 'sales_tl'].includes(user.role) ? <CustomerImportPage /> : renderDashboard()} />
          {/* <Route path="/alerts" element={<AlertsPage />} /> */}
          {/* <Route path="/reports" element={<ReportsPage role={user.role} />} /> */}
          <Route path="/settings" element={user.role === 'admin' ? <SettingsPage user={user} /> : renderDashboard()} />
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </div>
    </div>
  );
}

function AppSkeleton() {
  return (
    <div className="app-shell" style={{ overflow: 'hidden', height: '100vh' }}>
      <style>{`
        @keyframes shimmer {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
        .skeleton-shimmer {
          background: linear-gradient(90deg, #f1f5f9 25%, #e2e8f0 37%, #f1f5f9 63%);
          background-size: 400% 100%;
          animation: shimmer 1.4s ease infinite;
        }
        .skeleton-shimmer-dark {
          background: linear-gradient(90deg, #1e293b 25%, #334155 37%, #1e293b 63%);
          background-size: 400% 100%;
          animation: shimmer 1.4s ease infinite;
        }
      `}</style>

      {/* Sidebar Skeleton (desktop only - matches CSS responsive behavior) */}
      <aside className="sidebar" style={{ borderRight: '1px solid rgba(255, 255, 255, 0.06)' }}>
        <div className="sidebar-logo">
          <div className="brand" style={{ opacity: 0.6 }}><span>M</span>eetel</div>
          <div className="sub" style={{ opacity: 0.4 }}>Sales Management System</div>
        </div>
        <div className="sidebar-nav" style={{ padding: '20px 18px', gap: '16px', display: 'flex', flexDirection: 'column' }}>
          {[...Array(6)].map((_, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div className="skeleton-shimmer-dark" style={{ width: '18px', height: '18px', borderRadius: '4px' }} />
              <div className="skeleton-shimmer-dark" style={{ width: i % 2 === 0 ? '90px' : '70px', height: '14px', borderRadius: '3px' }} />
            </div>
          ))}
          <div style={{ marginTop: 'auto', display: 'flex', alignItems: 'center', gap: '12px', borderTop: '1px solid rgba(255, 255, 255, 0.06)', paddingTop: '20px' }}>
            <div className="skeleton-shimmer-dark" style={{ width: '18px', height: '18px', borderRadius: '4px' }} />
            <div className="skeleton-shimmer-dark" style={{ width: '60px', height: '14px', borderRadius: '3px' }} />
          </div>
        </div>
      </aside>

      {/* Main Content Skeleton */}
      <div className="main-content" style={{ display: 'flex', flexDirection: 'column', height: '100vh', width: '100%' }}>
        {/* Topbar Skeleton */}
        <header className="topbar" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div className="skeleton-shimmer" style={{ width: '120px', height: '20px', borderRadius: '4px' }} />
          </div>
          <div className="topbar-right" style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
            <div className="skeleton-shimmer" style={{ width: '30px', height: '30px', borderRadius: '8px' }} />
            <div className="skeleton-shimmer" style={{ width: '34px', height: '34px', borderRadius: '50%' }} />
            <div className="hide-mobile" style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <div className="skeleton-shimmer" style={{ width: '70px', height: '12px', borderRadius: '3px' }} />
              <div className="skeleton-shimmer" style={{ width: '40px', height: '9px', borderRadius: '2px' }} />
            </div>
          </div>
        </header>

        {/* Page Body Skeleton */}
        <div className="page-body" style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '24px' }}>
          {/* Header Area */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <div className="skeleton-shimmer" style={{ width: '160px', height: '24px', borderRadius: '6px' }} />
            <div className="skeleton-shimmer" style={{ width: '280px', height: '14px', borderRadius: '4px' }} />
          </div>

          {/* Stats Grid Skeleton */}
          <div className="stats-grid">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="stat-card" style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <div className="skeleton-shimmer" style={{ width: '80px', height: '12px', borderRadius: '3px' }} />
                <div className="skeleton-shimmer" style={{ width: '120px', height: '28px', borderRadius: '6px', margin: '4px 0' }} />
                <div className="skeleton-shimmer" style={{ width: '50px', height: '10px', borderRadius: '2px' }} />
              </div>
            ))}
          </div>

          {/* Dashboard Main Grid Skeleton */}
          <div className="dashboard-grid" style={{ flex: 1 }}>
            {[...Array(2)].map((_, i) => (
              <div key={i} className="card" style={{ display: 'flex', flexDirection: 'column', height: '340px' }}>
                <div className="card-header">
                  <div className="skeleton-shimmer" style={{ width: '120px', height: '16px', borderRadius: '4px' }} />
                </div>
                <div className="card-body" style={{ display: 'flex', flexDirection: 'column', gap: '16px', flex: 1, justifyContent: 'center' }}>
                  <div className="skeleton-shimmer" style={{ width: '100%', height: '40px', borderRadius: '6px' }} />
                  <div className="skeleton-shimmer" style={{ width: '100%', height: '40px', borderRadius: '6px' }} />
                  <div className="skeleton-shimmer" style={{ width: '100%', height: '40px', borderRadius: '6px' }} />
                  <div className="skeleton-shimmer" style={{ width: '100%', height: '40px', borderRadius: '6px' }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function App() {
  const [user, setUser] = useState(() => {
    const stored = sessionStorage.getItem('user');
    return stored ? JSON.parse(stored) : null;
  });

  const [initializing, setInitializing] = useState(true);

  // Sync user state to sessionStorage
  useEffect(() => {
    if (user) {
      sessionStorage.setItem('user', JSON.stringify(user));
    } else {
      sessionStorage.removeItem('user');
    }
  }, [user]);

  // Verify profile on load
  useEffect(() => {
    const token = tokenStore.get();
    if (!token) {
      setUser(null);
      setInitializing(false);
      return;
    }

    authApi.profile()
      .then(({ user: profile }) => {
        if (profile) {
          setUser(profile);
        } else {
          setUser(null);
        }
      })
      .catch((err) => {
        console.error('Failed to verify profile:', err);
        setUser(null);
      })
      .finally(() => {
        setInitializing(false);
      });
  }, []);

  if (initializing) {
    return <AppSkeleton />;
  }

  return (
    <QueryClientProvider client={queryClient}>
      <Toaster position="top-right" toastOptions={{ duration: 3500 }} />
      <Routes>
        <Route
          path="/login"
          element={
            user
              ? <Navigate to="/dashboard" replace />
              : <LoginPage onLogin={(u) => setUser(u)} />
          }
        />
        <Route
          path="/*"
          element={<ProtectedLayout user={user} setUser={setUser} />}
        />
      </Routes>
    </QueryClientProvider>
  );
}
